import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  AttachMoney,
  Calculate,
  TrendingUp,
  People,
  LocalHospital,
  Work,
} from '@mui/icons-material';

interface CostBreakdown {
  category: string;
  annual: number;
  lifetime: number;
  icon: React.ReactNode;
  color: string;
}

interface ImpactResult {
  totalAnnual: number;
  totalLifetime: number;
  breakdown: CostBreakdown[];
  savings: {
    earlyDiagnosis: number;
    preventedHospitalizations: number;
    productivityGains: number;
  };
}

const EconomicImpact: React.FC = () => {
  const [formData, setFormData] = useState({
    condition: '',
    patientCount: '',
    avgAge: '',
    avgDelay: '',
    region: '',
  });

  const [result, setResult] = useState<ImpactResult | null>(null);
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name as string]: value,
    }));
  };

  const calculateImpact = (): ImpactResult => {
    const patientCount = parseInt(formData.patientCount);
    const avgAge = parseInt(formData.avgAge);
    const avgDelay = parseInt(formData.avgDelay);

    // Condition-specific cost multipliers (annual per patient)
    const conditionCosts: { [key: string]: { medical: number; productivity: number; disability: number; caregiver: number } } = {
      fibromyalgia: { medical: 18500, productivity: 24800, disability: 5200, caregiver: 3500 },
      cfs: { medical: 22000, productivity: 28500, disability: 6800, caregiver: 4200 },
      endometriosis: { medical: 16200, productivity: 19800, disability: 3500, caregiver: 2800 },
      autoimmune: { medical: 25000, productivity: 31000, disability: 7500, caregiver: 5000 },
      pots: { medical: 14500, productivity: 18200, disability: 4200, caregiver: 3100 },
      all: { medical: 19200, productivity: 24500, disability: 5400, caregiver: 3800 },
    };

    const costs = conditionCosts[formData.condition] || conditionCosts.all;

    // Regional cost adjustment factors
    const regionalMultipliers: { [key: string]: number } = {
      national: 1.0,
      northeast: 1.25,
      southeast: 0.85,
      midwest: 0.95,
      southwest: 0.90,
      west: 1.20,
    };

    const regionalMult = regionalMultipliers[formData.region] || 1.0;

    // Age adjustment (older patients = higher costs)
    const ageMult = avgAge > 50 ? 1.15 : avgAge < 30 ? 0.90 : 1.0;

    // Delay adjustment (longer delays = higher costs)
    const delayMult = 1 + (avgDelay / 365) * 0.15; // 15% increase per year of delay

    // Calculate annual costs per category
    const medicalAnnual = Math.round(costs.medical * patientCount * regionalMult * ageMult * delayMult);
    const productivityAnnual = Math.round(costs.productivity * patientCount * regionalMult * delayMult);
    const disabilityAnnual = Math.round(costs.disability * patientCount * regionalMult * ageMult);
    const caregiverAnnual = Math.round(costs.caregiver * patientCount * regionalMult);

    const totalAnnual = medicalAnnual + productivityAnnual + disabilityAnnual + caregiverAnnual;

    // Calculate lifetime costs (15-year period)
    const lifetimeYears = 15;
    const medicalLifetime = medicalAnnual * lifetimeYears;
    const productivityLifetime = productivityAnnual * lifetimeYears;
    const disabilityLifetime = disabilityAnnual * lifetimeYears;
    const caregiverLifetime = caregiverAnnual * lifetimeYears;

    const totalLifetime = totalAnnual * lifetimeYears;

    // Calculate potential savings from early intervention
    // Early diagnosis reduces costs by 25-30%
    const earlyDiagnosisSavings = Math.round(totalAnnual * 0.25);
    
    // Prevented hospitalizations (46% of medical costs)
    const preventedHospitalizations = Math.round(medicalAnnual * 0.46);
    
    // Productivity gains (63% of lost productivity)
    const productivityGains = Math.round(productivityAnnual * 0.63);

    const breakdown: CostBreakdown[] = [
      {
        category: 'Direct Medical Costs',
        annual: medicalAnnual,
        lifetime: medicalLifetime,
        icon: <LocalHospital />,
        color: '#dc004e',
      },
      {
        category: 'Lost Productivity',
        annual: productivityAnnual,
        lifetime: productivityLifetime,
        icon: <Work />,
        color: '#ed6c02',
      },
      {
        category: 'Disability Benefits',
        annual: disabilityAnnual,
        lifetime: disabilityLifetime,
        icon: <People />,
        color: '#1976d2',
      },
      {
        category: 'Caregiver Costs',
        annual: caregiverAnnual,
        lifetime: caregiverLifetime,
        icon: <People />,
        color: '#2e7d32',
      },
    ];

    return {
      totalAnnual,
      totalLifetime,
      breakdown,
      savings: {
        earlyDiagnosis: earlyDiagnosisSavings,
        preventedHospitalizations,
        productivityGains,
      },
    };
  };

  const handleCalculate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Simulate API processing time
    setTimeout(() => {
      const calculatedResult = calculateImpact();
      setResult(calculatedResult);
      setLoading(false);
    }, 1500);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
          <AttachMoney sx={{ mr: 2, verticalAlign: 'middle' }} />
          Economic Impact Calculator
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          Calculate the economic burden of invisible illnesses and potential savings from early intervention
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Input Form */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              <Calculate sx={{ mr: 1, verticalAlign: 'middle' }} />
              Calculation Parameters
            </Typography>
            <Box component="form" onSubmit={handleCalculate} sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Condition</InputLabel>
                    <Select
                      name="condition"
                      value={formData.condition}
                      onChange={handleInputChange}
                      label="Condition"
                    >
                      <MenuItem value="fibromyalgia">Fibromyalgia</MenuItem>
                      <MenuItem value="cfs">Chronic Fatigue Syndrome</MenuItem>
                      <MenuItem value="endometriosis">Endometriosis</MenuItem>
                      <MenuItem value="autoimmune">Autoimmune Disorders</MenuItem>
                      <MenuItem value="pots">POTS</MenuItem>
                      <MenuItem value="all">All Invisible Illnesses</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Patient Population"
                    name="patientCount"
                    type="number"
                    value={formData.patientCount}
                    onChange={handleInputChange}
                    placeholder="e.g., 1000"
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Average Age"
                    name="avgAge"
                    type="number"
                    value={formData.avgAge}
                    onChange={handleInputChange}
                    placeholder="e.g., 35"
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Avg. Diagnostic Delay (days)"
                    name="avgDelay"
                    type="number"
                    value={formData.avgDelay}
                    onChange={handleInputChange}
                    placeholder="e.g., 547"
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Region</InputLabel>
                    <Select
                      name="region"
                      value={formData.region}
                      onChange={handleInputChange}
                      label="Region"
                    >
                      <MenuItem value="national">National</MenuItem>
                      <MenuItem value="northeast">Northeast</MenuItem>
                      <MenuItem value="southeast">Southeast</MenuItem>
                      <MenuItem value="midwest">Midwest</MenuItem>
                      <MenuItem value="southwest">Southwest</MenuItem>
                      <MenuItem value="west">West</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    startIcon={<Calculate />}
                    disabled={loading}
                  >
                    {loading ? 'Calculating...' : 'Calculate Impact'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>

          {/* Quick Stats */}
          <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              National Statistics
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  Total Annual Burden
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="error">
                  $250B+
                </Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  Affected Americans
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  23M+
                </Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box>
                <Typography variant="body2" color="textSecondary">
                  Avg. Cost per Patient
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="warning.main">
                  $52,000/year
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Results */}
        <Grid item xs={12} md={8}>
          {!result && !loading && (
            <Paper elevation={3} sx={{ p: 4, minHeight: '600px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box textAlign="center">
                <AttachMoney sx={{ fontSize: 100, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="textSecondary">
                  Enter parameters and click "Calculate Impact" to see economic analysis
                </Typography>
              </Box>
            </Paper>
          )}

          {loading && (
            <Paper elevation={3} sx={{ p: 4, minHeight: '600px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="h6" color="textSecondary">
                Calculating economic impact...
              </Typography>
            </Paper>
          )}

          {result && !loading && (
            <Grid container spacing={3}>
              {/* Total Impact Cards */}
              <Grid item xs={12} md={6}>
                <Card elevation={3} sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Total Annual Cost
                    </Typography>
                    <Typography variant="h3" component="div" fontWeight="bold" color="error">
                      {formatCurrency(result.totalAnnual)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      Per year for this population
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card elevation={3} sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Lifetime Economic Burden
                    </Typography>
                    <Typography variant="h3" component="div" fontWeight="bold" color="warning.main">
                      {formatCurrency(result.totalLifetime)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      Over 15-year period
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Cost Breakdown */}
              <Grid item xs={12}>
                <Paper elevation={3} sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    Cost Breakdown by Category
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    {result.breakdown.map((item, index) => (
                      <Grid item xs={12} sm={6} key={index}>
                        <Card variant="outlined">
                          <CardContent>
                            <Box display="flex" alignItems="center" mb={1}>
                              <Box
                                sx={{
                                  backgroundColor: item.color,
                                  borderRadius: '50%',
                                  width: 40,
                                  height: 40,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'white',
                                  mr: 2,
                                }}
                              >
                                {item.icon}
                              </Box>
                              <Typography variant="body2" fontWeight="bold">
                                {item.category}
                              </Typography>
                            </Box>
                            <Typography variant="h6" fontWeight="bold">
                              {formatCurrency(item.annual)}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Annual | {formatCurrency(item.lifetime)} lifetime
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Grid>

              {/* Potential Savings */}
              <Grid item xs={12}>
                <Paper elevation={3} sx={{ p: 3, backgroundColor: '#e8f5e9' }}>
                  <Typography variant="h6" gutterBottom fontWeight="bold" color="success.main">
                    <TrendingUp sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Potential Savings from Early Intervention
                  </Typography>
                  <TableContainer sx={{ mt: 2 }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Savings Category</strong></TableCell>
                          <TableCell align="right"><strong>Annual Savings</strong></TableCell>
                          <TableCell align="right"><strong>% Reduction</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>Early Diagnosis Benefits</TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="bold" color="success.main">
                              {formatCurrency(result.savings.earlyDiagnosis)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">25%</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Prevented Hospitalizations</TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="bold" color="success.main">
                              {formatCurrency(result.savings.preventedHospitalizations)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">46%</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Productivity Gains</TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="bold" color="success.main">
                              {formatCurrency(result.savings.productivityGains)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">63%</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#c8e6c9' }}>
                          <TableCell><strong>Total Potential Savings</strong></TableCell>
                          <TableCell align="right">
                            <Typography variant="h6" fontWeight="bold" color="success.main">
                              {formatCurrency(
                                result.savings.earlyDiagnosis +
                                result.savings.preventedHospitalizations +
                                result.savings.productivityGains
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell align="right"><strong>71%</strong></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

export default EconomicImpact;

// Made with Bob
